/* eslint-disable @typescript-eslint/no-unused-vars */
import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import {
  CancelPopupType,
  CustomUploadFile, Document, DocumentDeletedType,
  HubCompany, SetDownloadURL, SetIsSelectAllDocumentsType, SortType,
} from 'Types/DocumentHubTypes';
import { DocumentHubState } from 'Types/GlobalTypes';
import {
  DocumentDeleteRequest, DocumentPreviewRequest, DocumentsRequest,
  GetDocumentDownloadRequest, RenameDocumentRequest, UploadRequest,
} from 'Types/RequestBodies';
import _ from 'lodash';

export const initialState: DocumentHubState = {
  documents: [],
  uploadedDocuments: [],
  documentsEnded: false,
  documentsLoaded: false,
  documentsLoading: false,
  sort: 'des',
  sortType: 'date',
  companyId: 0,
  companyName: '',
  companyLogo: '',
  chatgptAllowed: false,
  isAdmin: false,
  uploaded: false,
  uploading: false,
  drawerOpen: false,
  deleteing: false,
  deleteLoading: false,
  downloading: false,
  downloadUrl: '',
  drawerState: 'file',
  searchTerm: '',
  uploadDocuments: [],
  nextUploadDocuments: [],
  failedDocuments: [],
  successDocuments: [],
  batchSize: 0,
  batchUploading: false,
  uploadLink: '',
  cancelPopup: {} as CancelPopupType,
  uploadCancelled: false,
  downloadLoadingIds: [],
  fileDownloadURL: '',
  fileViewUrl: '',
  documentCount: 0,
  selectedDocumentsDelete: [],
  clearAll: false,
  searchTermDelete: '',
  searchCountDelete: 0,
  sortFlurryEvent: '',
  fileTypeFlurryEvent: [],
  flurryFlag: true,
  userDocumentCount: 0,
};

const documentHubSlice = createSlice({
  name: 'documentHub',
  initialState,
  reducers: {
    getDocuments(state, action: PayloadAction<DocumentsRequest>) {
      state.documentsLoading = true;
      state.documentsEnded = false;
      if (action.payload.offset === 0) {
        state.documents = [];
        state.documentsLoaded = false;
      }
    },
    setDocuments(state, action: PayloadAction<Document[]>) {
      const { payload } = action;
      state.uploadedDocuments = state.uploadedDocuments.map((uploadedDoc) => ({
        ...uploadedDoc,
        isSelected: false,
      }));
      state.uploadedDocuments = state.uploadedDocuments.filter(
        (uploadedDoc) => !payload.some((doc) => uploadedDoc.id === doc.id),
      );
      let newDocs = payload;
      newDocs = newDocs?.filter((doc) => !state.documents.some((d) => d.id === doc.id));
      if (state.clearAll) {
        newDocs = newDocs.map((doc) => ({
          ...doc,
          showPopover: true,
          isSelected: !state.selectedDocumentsDelete.includes(doc.id) && doc.deleteAllowed,
        }));
      } else {
        newDocs = newDocs.map((
          doc,
        ) => ({
          ...doc,
          showPopover: true,
          isSelected:
         state.selectedDocumentsDelete.includes(doc.id),
        }));
      }
      state.documents = state.documents.concat(newDocs);
      state.documentsLoading = false;
      if (!payload.length) {
        state.documentsEnded = true;
      }
      state.documentsLoaded = true;
    },
    setHubCompany(state, action: PayloadAction<HubCompany>) {
      const {
        companyLogo, companyName, companyId, chatgptAllowed, documentCount,
        isAdmin, userDocumentCount,
      } = action.payload;
      state.companyId = companyId;
      state.companyLogo = companyLogo;
      state.companyName = companyName;
      state.chatgptAllowed = chatgptAllowed;
      state.documentCount = documentCount;
      state.isAdmin = isAdmin;
      state.userDocumentCount = userDocumentCount;
    },
    toggleSort(state, action:PayloadAction<SortType>) {
      if (action.payload === state.sortType) {
        if (state.sort === 'asc') state.sort = 'des';
        else state.sort = 'asc';
      } else {
        state.sortType = action.payload;
        state.sort = 'des';
      }
      state.documentsLoaded = false;
      state.documents = [];
      state.uploadedDocuments = [];
      if (state.sortType === 'date') {
        state.sortFlurryEvent = `date_${state.sort === 'asc' ? 'ascending' : 'descending'}`;
      } else if (state.sortType === 'name') {
        state.sortFlurryEvent = `name_${state.sort === 'asc' ? 'ascending' : 'descending'}`;
      } else if (state.sortType === 'size') {
        state.sortFlurryEvent = `filesize_${state.sort === 'asc' ? 'ascending' : 'descending'}`;
      } else if (state.sortType === 'status') {
        state.sortFlurryEvent = `status_${state.sort === 'asc' ? 'ascending' : 'descending'}`;
      } else if (state.sortType === 'uploadedBy') {
        state.sortFlurryEvent = `uploadedBy_${state.sort === 'asc' ? 'ascending' : 'descending'}`;
      }
    },
    uploadDocument(state, action:PayloadAction<UploadRequest>) {
      state.flurryFlag = false;
      state.uploaded = false;
      if (action.payload) {
        state.uploading = true;
      }
      if (action.payload.fileUrl) {
        state.batchUploading = true;
      }
    },
    documentUploaded(state, action:PayloadAction<Document>) {
      state.uploaded = true;
      state.uploading = false;
      if (!_.isEmpty(action.payload) && state.documentsLoaded) {
        state.uploadedDocuments = [{
          ...action.payload, showBlip: true, showPopover: true,
        },
        ...state.uploadedDocuments];
        state.documentCount += 1;
        state.userDocumentCount += 1;
      }
    },
    setDrawerOpen(state, action:PayloadAction<boolean>) {
      state.drawerOpen = action.payload;
      if (!action.payload) {
        state.drawerState = 'file';
      }
    },
    deleteDocument(state, action:PayloadAction<DocumentDeleteRequest>) {
      const { id } = action.payload;
      if (id) {
        if (state.clearAll) {
          state.documents = state.documents.map((doc) => ({
            ...doc,
            deleting: !id.includes(doc.id),
          }));
          state.uploadedDocuments = state.uploadedDocuments.map((doc) => ({
            ...doc,
            deleting: !id.includes(doc.id),
          }));
        } else {
          state.documents = state.documents.map((doc) => ({
            ...doc,
            deleting: id.includes(doc.id),
          }));

          state.uploadedDocuments = state.uploadedDocuments.map((doc) => ({
            ...doc,
            deleting: id.includes(doc.id),
          }));
        }
        state.deleteing = true;
        state.deleteLoading = true;
        state.clearAll = false;
      }
    },
    documentDeleted(state, action:PayloadAction<DocumentDeletedType>) {
      const {
        deletedDocumentIds, searchCountDelete,
      } = action.payload;

      state.documents = state.documents.map((doc) => ({
        ...doc,
        deleting: false,
      })).filter((doc) => !deletedDocumentIds.includes(doc.id));
      state.uploadedDocuments = state.uploadedDocuments.map((doc) => ({
        ...doc,
        deleting: false,
      })).filter((doc) => !deletedDocumentIds.includes(doc.id));
      state.documentCount = searchCountDelete ?? (state.documentCount - deletedDocumentIds.length);
      state.selectedDocumentsDelete = [];
      state.deleteing = false;
      state.deleteLoading = false;
      state.clearAll = false;
      state.userDocumentCount -= deletedDocumentIds.length;
    },
    documentDeleteFail(state, action:PayloadAction<number[]>) {
      state.deleteing = false;
      state.deleteLoading = false;
      const id = action.payload;
      if (id) {
        state.documents = state.documents.map((doc) => ({
          ...doc,
          deleting: false,
        }));

        state.uploadedDocuments = state.uploadedDocuments.map((doc) => ({
          ...doc,
          deleting: false,
        }));
      }
    },
    downloadDocument(state, action:PayloadAction<string>) {
      const link = action.payload;
      if (link) {
        state.documents = state.documents.map((doc) => ({
          ...doc,
          downloading: doc.fileUrl === link,
        }));
        state.uploadedDocuments = state.uploadedDocuments.map((doc) => ({
          ...doc,
          downloading: doc.fileUrl === link,
        }));
        state.downloading = true;
        state.downloadUrl = link;
      }
    },
    getPreviewDocument(state, action:PayloadAction<DocumentPreviewRequest>) {
    },
    clearPreviewDocument(state) {
      state.fileViewUrl = '';
    },
    documentDownloaded(state) {
      state.documents = state.documents.map((doc) => ({
        ...doc,
        downloading: false,
      }));
      state.uploadedDocuments = state.uploadedDocuments.map((doc) => ({
        ...doc,
        downloading: false,
      }));
      state.downloading = false;
      state.downloadUrl = '';
      state.fileDownloadURL = '';
    },
    renameDocument(state, action: PayloadAction<RenameDocumentRequest>) {
      const { id } = action.payload;
      state.documents = state.documents.map((doc) => ({
        ...doc,
        deleting: doc.id === id,
      }));
      state.uploadedDocuments = state.uploadedDocuments.map((doc) => ({
        ...doc,
        deleting: doc.id === id,
      }));
    },
    documentRenamed(state, action: PayloadAction<Document>) {
      const { id } = action.payload;
      if (id) {
        state.documents = state.documents
          .map((doc) => (doc.id === id ? { ...action.payload, showPopover: true } : doc));
        state.uploadedDocuments = state.uploadedDocuments
          .map((doc) => (doc.id === id ? { ...action.payload, showPopover: true } : doc));
      }
    },
    getDocumentDownloadURL(state, action: PayloadAction<GetDocumentDownloadRequest>) {
      state.downloadLoadingIds = [...state.downloadLoadingIds, action.payload.id];
    },
    setDocumentDownloadURL(state, action: PayloadAction<SetDownloadURL>) {
      const { fileURL, documentId, fileViewUrl } = action.payload;
      state.fileDownloadURL = fileURL;
      state.downloadLoadingIds = state.downloadLoadingIds.filter((docId) => docId !== documentId);
    },
    setDocumentPreviewURL(state, action: PayloadAction<SetDownloadURL>) {
      const { fileViewUrl } = action.payload;
      state.fileViewUrl = fileViewUrl;
    },
    setDrawerState(state, action: PayloadAction<string>) {
      state.drawerState = action.payload;
    },
    setSearchText(state, action: PayloadAction<string>) {
      state.searchTerm = action.payload;
      state.documentsLoaded = false;
      state.documents = [];
      state.uploadedDocuments = [];
    },
    setUploadDocument(state, action: PayloadAction<File>) {
      state.uploadDocuments.push(action.payload);
      if (state.uploadDocuments.length) {
        state.batchUploading = true;
      }
    },
    setNextUploadDocument(state, action: PayloadAction<File>) {
      state.nextUploadDocuments.push(action.payload);
    },
    resetNextUploadDocuments(state) {
      state.nextUploadDocuments = [];
    },
    removeUploadDocument(state) {
      state.uploadDocuments = state.uploadDocuments?.slice(1);
      if (!state.uploadDocuments.length) {
        state.batchUploading = false;
      }
    },
    modifyUploadDocument(state, action: PayloadAction<File>) {
      if (state.uploadDocuments.length === 1) {
        state.uploadDocuments[0] = action.payload;
      }
    },
    setFailDocuments(state, action: PayloadAction<CustomUploadFile>) {
      state.failedDocuments.push(action.payload);
    },
    setSuccessDocuments(state, action: PayloadAction<Document>) {
      state.successDocuments.push(action.payload);
    },
    setBatchSize(state, action: PayloadAction<number>) {
      state.batchSize = action.payload;
    },
    setBatchUploading(state, action: PayloadAction<boolean>) {
      state.batchUploading = action.payload;
    },
    setUploadLink(state, action: PayloadAction<string>) {
      state.uploadLink = action.payload;
    },
    setCancelPopup(state, action: PayloadAction<CancelPopupType>) {
      state.cancelPopup = action.payload;
    },
    toggleDocumentPopover(state, action: PayloadAction<number>) {
      state.documents = state.documents.map((doc) => ({
        ...doc,
        showPopover: doc.id === action.payload
          ? !doc.showPopover : true,
      }));
      state.uploadedDocuments = state.uploadedDocuments.map((doc) => ({
        ...doc,
        showPopover: doc.id === action.payload
          ? !doc.showPopover : true,
      }));
    },
    cancelUpload(state) {
      state.uploadDocuments = [];
      state.uploadCancelled = true;
    },
    resetBatch(state) {
      state.batchUploading = false;
      state.batchSize = 0;
      state.uploadDocuments = [];
      state.successDocuments = [];
      state.failedDocuments = [];
      state.uploadLink = '';
      state.fileTypeFlurryEvent = [];
      state.flurryFlag = true;
    },
    setSelectedDocuments(state, action: PayloadAction<number[]>) {
      state.selectedDocumentsDelete = action.payload;
    },
    setIsSelectedDocument(state, action: PayloadAction<number>) {
      const documentIndex = state.documents.findIndex(
        (item) => item.id === action.payload,
      );
      if (documentIndex !== -1) {
        state.documents[documentIndex].isSelected = !state.documents[documentIndex].isSelected;
      } else {
        const index = state.uploadedDocuments.findIndex(
          (item) => item.id === action.payload,
        );
        state.uploadedDocuments[index].isSelected = !state.uploadedDocuments[index].isSelected;
      }
    },
    setIsSelectAllDocuments(state, action: PayloadAction<SetIsSelectAllDocumentsType>) {
      const { selectedDocuments, checkSelectAll } = action.payload;
      selectedDocuments.forEach((selectedDocumentId) => {
        const documentIndex = state.documents.findIndex((
          document,
        ) => document.id === selectedDocumentId);

        if (documentIndex !== -1 && state.documents[documentIndex]?.isSelected !== undefined) {
          state.documents[documentIndex].isSelected = checkSelectAll;
        } else {
          const Index = state.uploadedDocuments.findIndex((
            document,
          ) => document.id === selectedDocumentId);
          state.uploadedDocuments[Index].isSelected = checkSelectAll;
        }
      });
    },
    setClearAllDocuments(state, action: PayloadAction<boolean>) {
      const clearAllPayload = action.payload;
      if (clearAllPayload) {
        state.clearAll = true;
      } else {
        state.clearAll = false;
      }
    },
    setSearchTerm(state, action: PayloadAction<string>) {
      state.searchTermDelete = action.payload;
    },
    setSearchCount(state, action: PayloadAction<number>) {
      state.searchCountDelete = action.payload;
    },
    resetUploadCancel(state) {
      state.uploadCancelled = false;
    },
    pushFileTypeFlurry(state, action: PayloadAction<string>) {
      state.fileTypeFlurryEvent.push(action.payload);
    },
    reset: (state) => ({
      ...initialState,
      uploaded: state.uploaded,
      uploading: state.uploading,
    }),
  },
});
export const { actions, reducer, name: sliceKey } = documentHubSlice;
